import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";

// Warn loudly when someone uses the PREVIEW build so test data is never
// mistaken for live production data (they use separate databases).
const IS_PREVIEW = typeof window !== "undefined" && window.location.hostname.includes("preview.emergentagent.com");
const PreviewBanner = () => IS_PREVIEW ? (
  <div
    data-testid="preview-banner"
    className="sticky top-0 z-[100] bg-amber-500 text-black text-center text-xs sm:text-sm font-semibold py-1.5 px-3"
  >
    PREVIEW / TEST SITE — data here is NOT live. Live site: instantpais.com
  </div>
) : null;

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import CustomerDashboard from "@/pages/CustomerDashboard";
import Apply from "@/pages/Apply";
import Submitted from "@/pages/Submitted";
import OfferFlow from "@/pages/OfferFlow";
import Repay from "@/pages/Repay";
import PaymentResult from "@/pages/PaymentResult";
import RepayResult from "@/pages/RepayResult";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminApplicationDetail from "@/pages/admin/AdminApplicationDetail";
import AdminRepayments from "@/pages/admin/AdminRepayments";
import AdminRepaymentSettings from "@/pages/admin/AdminRepaymentSettings";
import AdminLoans from "@/pages/admin/AdminLoans";
import AdminAuditLogs from "@/pages/admin/AdminAuditLogs";
import AdminChangeCreds from "@/pages/admin/AdminChangeCreds";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminPaymentSettings from "@/pages/admin/AdminPaymentSettings";

import AdminChats from "@/pages/admin/AdminChats";
import AdminChatDetail from "@/pages/admin/AdminChatDetail";
import AdminDisburse from "@/pages/admin/AdminDisburse";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import SupportChat from "@/pages/SupportChat";

function App() {
  useEffect(() => {
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("theme-color", "#0B4F37");
    setMeta("description", "Instant Paisa — quick personal loans from ₹1,000 to ₹1,00,000. Transparent fees, reviewed by our team.");
    setMeta("apple-mobile-web-app-capable", "yes");
    setMeta("apple-mobile-web-app-status-bar-style", "black-translucent");
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <PreviewBanner />
          <Toaster position="top-center" richColors />
          <Routes>
            {/* Public — customer */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Protected — customer */}
            <Route path="/" element={<ProtectedRoute role="customer"><CustomerDashboard /></ProtectedRoute>} />
            <Route path="/apply" element={<ProtectedRoute role="customer"><Apply /></ProtectedRoute>} />
            <Route path="/submitted" element={<ProtectedRoute role="customer"><Submitted /></ProtectedRoute>} />
            <Route path="/offer" element={<ProtectedRoute role="customer"><OfferFlow /></ProtectedRoute>} />
            <Route path="/repay" element={<ProtectedRoute role="customer"><Repay /></ProtectedRoute>} />
            <Route path="/support" element={<ProtectedRoute role="customer"><SupportChat /></ProtectedRoute>} />
            <Route path="/repay-result" element={<ProtectedRoute role="customer"><RepayResult /></ProtectedRoute>} />
            <Route path="/payment-result" element={<ProtectedRoute role="customer"><PaymentResult /></ProtectedRoute>} />

            {/* Public — admin */}
            <Route path="/secure-admin/login" element={<AdminLogin />} />
            <Route path="/admin/forgot-password" element={<ForgotPassword admin />} />
            <Route path="/admin/reset-password" element={<ResetPassword admin />} />

            {/* Protected — admin */}
            <Route path="/admin" element={<ProtectedRoute role="admin"><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="applications/:id" element={<AdminApplicationDetail />} />
              <Route path="repayments" element={<AdminRepayments />} />
              <Route path="payments" element={<AdminPayments />} />
              <Route path="chats" element={<AdminChats />} />
              <Route path="chats/:cid" element={<AdminChatDetail />} />
              <Route path="payment-settings" element={<AdminPaymentSettings />} />
              <Route path="repayment-settings" element={<AdminRepaymentSettings />} />
              <Route path="loans" element={<AdminLoans />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="disburse" element={<AdminDisburse />} />
              <Route path="audit-logs" element={<AdminAuditLogs />} />
              <Route path="settings" element={<AdminChangeCreds />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
